import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Alert, Button, Card, Checkbox, Form, InputNumber, Modal, Select, Spin, Switch, Table, Tag, Tooltip } from 'antd';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { useAuthStore } from '@/store/useAuthStore';
import { STATUS_COLORS, date, errorText, money } from './format';
import CalculationSettingsCard from './CalculationSettingsCard';

const at = (v) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—');
const projectText = (p, t) => (p ? [p.code, p.name].filter(Boolean).join(' ') : t('notify.noProject'));

/** A list of addresses in one cell: the first ones, the rest in a tooltip. */
const Emails = ({ list = [] }) =>
  list.length ? (
    <Tooltip title={list.join(', ')} placement="topLeft">
      <span>{list.join(', ')}</span>
    </Tooltip>
  ) : (
    '—'
  );

/**
 * Mails to the client's people about returns to pay: the switch, the reminder
 * and its CC, who hears about each project, what a run would send right now
 * (nothing is sent from here) and what went out. Sending is off until
 * tech-admin switches it on; switching on marks what is already on the
 * platform as known, unless the open returns are to be announced too.
 */
export default function NotificationsPanel({ company }) {
  const { t } = useTranslation('taxReturns');
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const roles = useAuthStore((state) => state.user?.roles) || [];
  const canSwitch = roles.some((r) => ['techAdmin', 'superAdmin'].includes(typeof r === 'string' ? r : r?.key));
  const [mailOf, setMailOf] = useState(null);
  const [switchOn, setSwitchOn] = useState(false);
  const [announceOpen, setAnnounceOpen] = useState(false);

  const key = ['tax-returns-notify', company];
  const { data: s, error } = useQuery({
    queryKey: key,
    queryFn: () => API.taxReturns.getNotificationSettings(company),
    enabled: Boolean(company),
  });
  const preview = useQuery({
    queryKey: [...key, 'preview'],
    queryFn: () => API.taxReturns.previewNotifications(company),
    enabled: Boolean(company),
  });
  const log = useQuery({
    queryKey: [...key, 'log'],
    queryFn: () => API.taxReturns.getNotificationLog(company),
    enabled: Boolean(company),
  });
  const mail = useQuery({
    queryKey: [...key, 'mail', mailOf?.kind, mailOf?.project],
    queryFn: () => API.taxReturns.renderNotificationMail(company, mailOf.kind, mailOf.project),
    enabled: Boolean(mailOf),
  });

  useEffect(() => {
    if (!s) return;
    const extra = Object.fromEntries((s.extraRecipients || []).map((x) => [x.project || 'none', x.emails]));
    form.setFieldsValue({
      reminderDays: s.reminderDays,
      cc: s.cc,
      ccPosted: s.ccPosted,
      extra: Object.fromEntries(s.projects.map((p) => [p.project?._id || 'none', extra[p.project?._id || 'none'] || []])),
    });
  }, [s, form]);

  const save = useMutation({
    mutationFn: (data) => API.taxReturns.updateNotificationSettings(company, data),
    onSuccess: () => {
      setSwitchOn(false);
      setAnnounceOpen(false);
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  if (!s) return error ? <Alert type="error" showIcon message={errorText(t, error)} /> : <Spin />;

  const onSave = (v) =>
    save.mutate({
      reminderDays: v.reminderDays,
      cc: v.cc || [],
      ccPosted: Boolean(v.ccPosted),
      extraRecipients: Object.entries(v.extra || {}).map(([project, emails]) => ({
        project: project === 'none' ? null : project,
        emails: emails || [],
      })),
    });

  const blockedTag = (reason) =>
    reason ? <Tag color={reason === 'no_recipients' ? 'red' : 'default'}>{t(`notify.blocked.${reason}`)}</Tag> : <Tag color="green">{t('notify.blocked.none')}</Tag>;

  const groupColumns = [
    { title: t('fields.project'), dataIndex: 'project', width: 200, ellipsis: true, render: (p) => projectText(p, t) },
    { title: t('notify.returns'), width: 90, render: (g) => g.returns.length },
    { title: t('fields.amount'), dataIndex: 'amount', width: 150, align: 'right', render: money },
    { title: t('notify.to'), dataIndex: 'to', ellipsis: { showTitle: false }, render: (v) => <Emails list={v} /> },
    { title: t('notify.cc'), dataIndex: 'cc', width: 180, ellipsis: { showTitle: false }, render: (v) => <Emails list={v} /> },
    { title: t('notify.state'), dataIndex: 'blockedBy', width: 190, render: blockedTag },
    {
      title: '',
      width: 60,
      align: 'center',
      render: (g) => (
        <Tooltip title={t('notify.showMail')}>
          <Button
            shape="circle"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setMailOf({ kind: g.kind, project: g.project?._id || 'none' })}
          />
        </Tooltip>
      ),
    },
  ];

  const returnColumns = [
    { title: t('fields.title'), ellipsis: true, render: (r) => r.title || r.purpose },
    { title: t('fields.bop'), dataIndex: 'bop', width: 200 },
    { title: t('fields.dueDate'), dataIndex: 'dueDate', width: 110, render: date },
    { title: t('fields.amount'), dataIndex: 'amount', width: 140, align: 'right', render: money },
    {
      title: t('fields.status'),
      dataIndex: 'status',
      width: 140,
      render: (v) => <Tag color={STATUS_COLORS[v]}>{t(`status.${v}`)}</Tag>,
    },
    { title: t('notify.purposeOnSlip'), dataIndex: 'purposeOnSlip', width: 260, ellipsis: true },
  ];

  const p = preview.data;

  return (
    <div className="grid gap-4">
      {!s.clientView && <Alert type="warning" showIcon className="!static" message={t('notify.clientViewOff')} />}

      <Card
        size="small"
        title={t('notify.title')}
        extra={
          <Switch
            checked={s.enabled}
            disabled={!canSwitch}
            loading={save.isPending}
            checkedChildren={t('notify.on')}
            unCheckedChildren={t('notify.off')}
            onChange={(checked) => (checked ? setSwitchOn(true) : save.mutate({ enabled: false }))}
          />
        }
      >
        <p className="text-sm opacity-70" style={{ marginBottom: 12 }}>
          {t('notify.intro')}
        </p>
        {s.enabled ? (
          <Alert type="success" showIcon className="!static" style={{ marginBottom: 12 }} message={t('notify.isOn', { date: at(s.enabledAt) })} />
        ) : (
          <Alert type="info" showIcon className="!static" style={{ marginBottom: 12 }} message={t('notify.isOff')} />
        )}
        <Form form={form} layout="vertical" onFinish={onSave}>
          <div className="grid gap-4 md:grid-cols-3">
            <Form.Item name="reminderDays" label={t('notify.reminderDays')} tooltip={t('notify.reminderDaysHelp')}>
              <InputNumber min={1} max={10} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="cc" label={t('notify.ccLabel')} tooltip={t('notify.ccHelp')} className="md:col-span-2">
              <Select mode="tags" tokenSeparators={[',', ';', ' ']} placeholder="name@company.com" open={false} />
            </Form.Item>
          </div>
          <Form.Item name="ccPosted" valuePropName="checked">
            <Checkbox>{t('notify.ccPosted')}</Checkbox>
          </Form.Item>

          <Table
            size="small"
            tableLayout="fixed"
            rowKey={(r) => r.project?._id || 'none'}
            pagination={false}
            title={() => t('notify.recipientsTitle')}
            dataSource={s.projects}
            columns={[
              { title: t('fields.project'), dataIndex: 'project', width: 220, ellipsis: true, render: (v) => projectText(v, t) },
              {
                title: t('notify.approvers'),
                dataIndex: 'people',
                ellipsis: { showTitle: false },
                render: (people) =>
                  people.length ? (
                    <Tooltip title={people.map((x) => `${x.name} <${x.email}>`).join(', ')} placement="topLeft">
                      <span>{people.map((x) => x.name || x.email).join(', ')}</span>
                    </Tooltip>
                  ) : (
                    <span className="opacity-60">{t('notify.noApprovers')}</span>
                  ),
              },
              {
                title: t('notify.extra'),
                width: 380,
                render: (row) => (
                  <Form.Item name={['extra', row.project?._id || 'none']} noStyle>
                    <Select mode="tags" tokenSeparators={[',', ';', ' ']} style={{ width: '100%' }} placeholder="name@company.com" open={false} />
                  </Form.Item>
                ),
              },
            ]}
          />
          {save.isError && <Alert type="error" showIcon className="!static" message={errorText(t, save.error)} style={{ marginTop: 12 }} />}
          <div className="mt-3 flex justify-end">
            <Button shape="round" type="primary" htmlType="submit" loading={save.isPending}>
              {t('drive.save')}
            </Button>
          </div>
        </Form>
      </Card>

      <CalculationSettingsCard company={company} />

      <Card
        size="small"
        title={t('notify.previewTitle')}
        extra={
          <Button shape="round" size="small" icon={<ReloadOutlined />} onClick={() => preview.refetch()} loading={preview.isFetching}>
            {t('notify.refresh')}
          </Button>
        }
      >
        <p className="text-sm opacity-70" style={{ marginBottom: 12 }}>
          {t('notify.previewIntro')}
        </p>
        {!s.enabled && s.onSwitch && (
          <Alert type="info" showIcon className="!static" style={{ marginBottom: 12 }} message={t('notify.onSwitchNote', s.onSwitch)} />
        )}
        {preview.error && <Alert type="error" showIcon className="!static" message={errorText(t, preview.error)} />}
        {p && (
          <>
            <Table
              size="small"
              tableLayout="fixed"
              rowKey={(g) => `posted-${g.project?._id || 'none'}`}
              title={() => t('notify.postedTitle')}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              dataSource={p.posted}
              columns={groupColumns}
              locale={{ emptyText: t('notify.nothing') }}
              expandable={{
                expandedRowRender: (g) => (
                  <Table size="small" tableLayout="fixed" rowKey="_id" pagination={false} dataSource={g.returns} columns={returnColumns} />
                ),
              }}
            />
            <Table
              style={{ marginTop: 16 }}
              size="small"
              tableLayout="fixed"
              rowKey={(g) => `reminder-${g.project?._id || 'none'}`}
              title={() => t('notify.remindersTitle', { days: s.reminderDays })}
              pagination={false}
              dataSource={p.reminders}
              columns={groupColumns}
              locale={{ emptyText: t('notify.nothing') }}
              expandable={{
                expandedRowRender: (g) => (
                  <Table size="small" tableLayout="fixed" rowKey="_id" pagination={false} dataSource={g.returns} columns={returnColumns} />
                ),
              }}
            />
            <Table
              style={{ marginTop: 16 }}
              size="small"
              tableLayout="fixed"
              rowKey="_id"
              title={() => t('notify.upcomingTitle')}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              dataSource={p.upcoming}
              locale={{ emptyText: t('notify.nothing') }}
              columns={[
                { title: t('notify.reminderOn'), dataIndex: 'reminderDay', width: 120, render: (v) => date(v) },
                { title: t('fields.project'), dataIndex: 'project', width: 200, ellipsis: true, render: (v) => projectText(v, t) },
                { title: t('fields.title'), ellipsis: true, render: (r) => r.title || r.purpose },
                { title: t('fields.dueDate'), dataIndex: 'dueDate', width: 110, render: date },
                { title: t('fields.amount'), dataIndex: 'amount', width: 140, align: 'right', render: money },
                {
                  title: t('fields.status'),
                  dataIndex: 'status',
                  width: 140,
                  render: (v) => <Tag color={STATUS_COLORS[v]}>{t(`status.${v}`)}</Tag>,
                },
                { title: t('notify.to'), dataIndex: 'to', width: 220, ellipsis: { showTitle: false }, render: (v) => <Emails list={v} /> },
              ]}
            />
          </>
        )}
      </Card>

      <Card size="small" title={t('notify.logTitle')}>
        <Table
          size="small"
          tableLayout="fixed"
          rowKey="_id"
          loading={log.isLoading}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          dataSource={log.data || []}
          locale={{ emptyText: t('notify.logEmpty') }}
          columns={[
            { title: t('drive.when'), dataIndex: 'at', width: 140, render: at },
            { title: t('notify.kind'), dataIndex: 'kind', width: 130, render: (v) => t(`notify.kinds.${v}`) },
            { title: t('fields.project'), dataIndex: 'project', width: 180, ellipsis: true, render: (v) => projectText(v, t) },
            { title: t('notify.returns'), dataIndex: 'returns', width: 90 },
            { title: t('fields.amount'), dataIndex: 'amount', width: 140, align: 'right', render: money },
            { title: t('notify.to'), dataIndex: 'to', ellipsis: { showTitle: false }, render: (v) => <Emails list={v} /> },
            {
              title: t('notify.state'),
              width: 140,
              render: (x) =>
                x.outcome === 'queued' ? (
                  <Tag color="green">{t('notify.outcomes.queued')}</Tag>
                ) : (
                  <Tooltip title={x.error}>
                    <Tag color="red">{t('notify.outcomes.failed')}</Tag>
                  </Tooltip>
                ),
            },
          ]}
        />
      </Card>

      <Modal
        open={switchOn}
        title={t('notify.switchTitle')}
        okText={t('notify.switchOk')}
        cancelText={t('notify.cancel')}
        okButtonProps={{ shape: 'round' }}
        cancelButtonProps={{ shape: 'round' }}
        confirmLoading={save.isPending}
        onOk={() => save.mutate({ enabled: true, announceOpen })}
        onCancel={() => {
          setSwitchOn(false);
          setAnnounceOpen(false);
        }}
      >
        <p>{t('notify.switchText', s.onSwitch || { known: 0, open: 0 })}</p>
        <Checkbox checked={announceOpen} onChange={(e) => setAnnounceOpen(e.target.checked)}>
          {t('notify.announceOpen', { count: s.onSwitch?.open || 0 })}
        </Checkbox>
      </Modal>

      <Modal
        open={Boolean(mailOf)}
        width={760}
        footer={null}
        title={mail.data?.subject || t('notify.showMail')}
        onCancel={() => setMailOf(null)}
        destroyOnClose
      >
        {mail.isLoading && <Spin />}
        {mail.error && <Alert type="error" showIcon className="!static" message={errorText(t, mail.error)} />}
        {mail.data && (
          <>
            <p className="text-sm" style={{ marginBottom: 8 }}>
              <strong>{t('notify.to')}:</strong> {mail.data.to.join(', ') || '—'}
              {mail.data.cc.length > 0 && (
                <>
                  <br />
                  <strong>{t('notify.cc')}:</strong> {mail.data.cc.join(', ')}
                </>
              )}
            </p>
            <iframe
              title="mail"
              srcDoc={mail.data.html}
              sandbox=""
              style={{ width: '100%', height: '70vh', border: '1px solid #e5e7eb', borderRadius: 8 }}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
